import { useMemo, useState } from 'react';
import { Building2, ChevronDown, LogOut, Plus, User } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useUser } from '@descope/react-sdk';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useOrganizations } from '@/hooks/api/useOrganizations';
import { CreateOrganizationModal } from '../CreateOrganizationModal';
import { InviteAcceptModal } from '../InviteAcceptModal';
import { getUsername, cn, type DescopeUser } from '@/lib/utils';
import { useGuardedNavigation } from '@/hooks/useGuardedNavigation';
import { useScopeChangeAction } from '@/hooks/useScopeChangeHooks';
import { useAuth } from '@/hooks/useAuth';
import { UserDisplaySection } from './UserDisplaySection';
import { SwitcherItem } from './SwitcherItem';
import type { OrganizationList } from '@kirin/api-client/contracts/organizations';
import {
  persistLastViewedProfile,
  ProfileType,
} from '@/lib/profilePersistence';

type OrganizationType = OrganizationList[number];

export function OrganizationSwitcher() {
  const navigate = useGuardedNavigation();
  const { changeScopeTo } = useScopeChangeAction();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: organizations = [], isLoading: orgsLoading } =
    useOrganizations();
  const { user, isUserLoading } = useUser();
  const { signOut } = useAuth();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [inviteModalOrg, setInviteModalOrg] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Get display name from Descope user
  const displayName = getUsername(user as DescopeUser) || 'Personal';
  const userEmail = (user as DescopeUser)?.email || '';

  const currentOrg = useMemo(() => {
    if (organizationId) {
      return organizations.find(o => o.id === organizationId);
    }
    return null;
  }, [organizationId, organizations]);
  const isPersonal = !organizationId;

  const openNewOrganizationModal = () => {
    changeScopeTo({
      id: 'new-organization-creation-modal',
      run: () => setIsCreateModalOpen(true),
    });
  };

  const navigateToLink = (
    e: React.MouseEvent,
    link: string,
    profileId: string,
    profileType: ProfileType
  ) => {
    e.preventDefault();

    // Persist the profile selection
    persistLastViewedProfile({
      profileId,
      profileType,
    });

    navigate(link);
  };

  const handleLogout = () => {
    signOut();
  };

  const { invited: invitedOrganizations, regular: regularOrganizations } =
    useMemo(() => {
      const regular: OrganizationType[] = [];
      const invited: OrganizationType[] = [];
      organizations.forEach(org => {
        if (org.isInvited) {
          invited.push(org);
        } else {
          regular.push(org);
        }
      });
      return { regular, invited };
    }, [organizations]);

  const pendingInvitesCount = invitedOrganizations.length;

  if (orgsLoading || isUserLoading) {
    // Render a button with the same shape as the loaded state so
    // E2E locators (`header button:has-text("Personal"/"Organization")`,
    // `button[class*="outline"]`, etc.) match consistently across loading
    // and loaded states. The previous skeleton-div implementation broke
    // kirin_auto sanity (KIR-2470) — locators all targeted <button> and
    // missed the <div> during the orgs-loading window.
    //
    // Icon + chip derived from URL context (organizationId) — when on an
    // /<orgId>/* route the loading state should render Building2 + "Organization",
    // matching what the loaded state will render. aria-busy + animate-pulse
    // signal the loading state to AT + visual; deliberately not `disabled`
    // because that would make Playwright auto-wait click hang.
    const LoadingIcon = isPersonal ? User : Building2;
    const loadingChip = isPersonal ? 'Personal' : 'Organization';
    return (
      <Button
        variant="outline"
        aria-busy
        data-testid="org-switcher-trigger"
        data-loading="true"
        className="w-full flex items-center gap-2 min-w-0 px-2.5 py-1.5 animate-pulse"
      >
        <div className="flex h-6 w-6 text-muted-foreground items-center justify-center rounded-full bg-muted flex-shrink-0">
          <LoadingIcon className="h-3.5 w-3.5 text-inherit" />
        </div>
        <span className="text-sm truncate text-muted-foreground flex-1 min-w-0 text-left">
          Loading…
        </span>
        <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground flex-shrink-0">
          {loadingChip}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            data-testid="org-switcher-trigger"
            className="w-full flex items-center gap-2 min-w-0 px-2.5 py-1.5 relative"
          >
            {/* Icon with rounded bg */}
            <div className="flex h-6 w-6 text-muted-foreground items-center justify-center rounded-full bg-muted flex-shrink-0">
              {isPersonal ? (
                <User className="h-3.5 w-3.5 text-inherit" />
              ) : (
                <Building2 className="h-3.5 w-3.5 text-inherit" />
              )}
            </div>

            {/* Name with tooltip */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm truncate text-muted-foreground flex-1 min-w-0 text-left">
                  {isPersonal
                    ? displayName
                    : currentOrg?.name || 'Organization'}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isPersonal ? displayName : currentOrg?.name || 'Organization'}
              </TooltipContent>
            </Tooltip>

            {/* Chip */}
            <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground flex-shrink-0">
              {isPersonal ? 'Personal' : 'Organization'}
            </span>

            {/* Pending invites badge */}
            {pendingInvitesCount > 0 && (
              <span className="absolute -top-2 -right-1.5 h-5 w-5 bg-red-500 rounded-full border-2 border-background flex items-center justify-center">
                <span className="text-[10px] text-white font-semibold">
                  {pendingInvitesCount > 9 ? '9+' : pendingInvitesCount}
                </span>
              </span>
            )}

            {/* Dropdown icon */}
            <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-[300px] !overflow-y-visible"
        >
          {/* User Display Section */}
          <UserDisplaySection name={displayName} email={userEmail} />

          {/* Personal Account Section */}
          <div className="py-2">
            <div className="px-2 pb-1.5">
              <h3 className="text-xs font-medium text-muted-foreground">
                Personal Account
              </h3>
            </div>
            <DropdownMenuItem asChild className={cn(isPersonal && 'bg-accent')}>
              <Link
                to="/dashboard"
                onClick={e =>
                  navigateToLink(e, '/dashboard', 'personal', 'account')
                }
                className="cursor-pointer"
              >
                <SwitcherItem
                  name={displayName}
                  isActive={isPersonal}
                  icon={User}
                />
              </Link>
            </DropdownMenuItem>
          </div>

          {/* Organizations Section */}
          {regularOrganizations.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="py-2">
                <div className="px-2 pb-1.5">
                  <h3 className="text-xs font-medium text-muted-foreground">
                    Organizations
                  </h3>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                  {regularOrganizations.map(org => (
                    <DropdownMenuItem
                      key={org.id}
                      asChild
                      className={cn(organizationId === org.id && 'bg-accent')}
                    >
                      <Link
                        to={`/${org.id}`}
                        onClick={e =>
                          navigateToLink(
                            e,
                            `/${org.id}`,
                            org.id,
                            'organization'
                          )
                        }
                        className="cursor-pointer"
                      >
                        <SwitcherItem
                          name={org.name}
                          isActive={organizationId === org.id}
                          icon={Building2}
                        />
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Invited Organizations */}
          {invitedOrganizations.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="py-2">
                <div className="px-2 pb-1.5">
                  <h3 className="text-xs font-medium text-muted-foreground">
                    Pending Invites
                  </h3>
                </div>
                {invitedOrganizations.map(org => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() =>
                      setInviteModalOrg({ id: org.id, name: org.name })
                    }
                    className="cursor-pointer"
                  >
                    <SwitcherItem
                      name={org.name}
                      isActive={false}
                      icon={Building2}
                    />
                  </DropdownMenuItem>
                ))}
              </div>
            </>
          )}

          {/* Create Organization Button */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={openNewOrganizationModal}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create organization</span>
          </DropdownMenuItem>

          {/* Logout Button */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            className="flex items-center gap-2 text-destructive focus:text-destructive"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateOrganizationModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />

      {inviteModalOrg && (
        <InviteAcceptModal
          open={!!inviteModalOrg}
          onOpenChange={open => !open && setInviteModalOrg(null)}
          organizationId={inviteModalOrg.id}
          organizationName={inviteModalOrg.name}
        />
      )}
    </>
  );
}
